import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import formatDateTime from "components/Shared/formatDateTime"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation } from "react-router-dom"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { getPromotionDetails } from "store/actions/promotionsAction"

const PromotionDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t(`Discount Details | Sign Burger`)

  const location = useLocation()
  const dispatch = useDispatch()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, promotionDetails } = useSelector(state => state.Promotions)

  useEffect(() => {
    const fetchPromotionDetails = () => {
      const payload = {
        id,
      }
      dispatch(getPromotionDetails(payload))
    }
    fetchPromotionDetails()
  }, [])

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton /> {t("Discount Details")}
                </CardTitle>
                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={650} rounded />
                  ) : Object.values(promotionDetails).length === 0 ? (
                    <p className="text-center my-3">
                      {t("No discount details found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0 capitalize_td">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{promotionDetails._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name")}</th>
                          <td>{promotionDetails?.name}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            {promotionDetails?.status === 1 ? (
                              <Badge className="bg-success">
                                {t("Active")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Deactive")}
                              </Badge>
                            )}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Offer Type")}</th>
                          <td>{t(promotionDetails.offerType) || "-"}</td>
                        </tr>

                        {/* <tr>
                          <th scope="row">{t("Qualification Type")}</th>
                          <td>{promotionDetails.qualificationType || "-"}</td>
                        </tr> */}
                        <tr>
                          <th scope="row">{t("Discount Type")}</th>
                          <td>{t(promotionDetails.discountType) || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Amount Percentage")}</th>
                          <td>{promotionDetails.amount}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Maximum Amount")}</th>
                          <td>{promotionDetails.maxAmount ?? "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Minimum Cart Amount")}</th>
                          <td>{promotionDetails.minimumCartAmount ?? "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Order Type")}</th>
                          <td>
                            {promotionDetails?.orderType?.length === 0
                              ? "-"
                              : promotionDetails?.orderType?.map(
                                  item => item + ", "
                                )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Available Branches")}</th>
                          <td>
                            {promotionDetails?.availableBranches?.length === 0
                              ? "-"
                              : promotionDetails?.availableBranches?.map(item =>
                                  language === "sa"
                                    ? item.nameAr
                                    : item.name + ", "
                                )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Max Usage Of Customers")}</th>
                          <td>
                            {promotionDetails?.limits?.maxUsageOfCustomers ??
                              "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Max Usage Per Customer")}</th>
                          <td>
                            {promotionDetails?.limits?.maxUsagePerCustomer ??
                              "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Has Advanced Settings")}</th>
                          <td>
                            {promotionDetails.hasAdvancedSettings
                              ? t("Yes")
                              : t("No")}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Created At")}</th>
                          <td dir="ltr" className="text-start">
                            {formatDateTime(promotionDetails?.createdAt, language) ||
                              "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Updated At")}</th>
                          <td dir="ltr" className="text-start">
                            {promotionDetails?.updatedAt
                              ? formatDateTime(
                                  promotionDetails.updatedAt,
                                  language
                                )
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Updated By")}</th>
                          <td>
                            {promotionDetails?.lastUpdatedBy ? (
                              <>
                                <div>
                                  {promotionDetails.lastUpdatedBy.firstName}{" "}
                                  {promotionDetails.lastUpdatedBy.lastName}
                                </div>
                                <small className="text-muted" dir="ltr">
                                  {promotionDetails.lastUpdatedBy.email}
                                </small>
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          {/* SHOW CATEGORY & ITEMS */}
          {["category", "item"].includes(promotionDetails.offerType) && (
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle>
                    {t("Categories")}{" "}
                    {promotionDetails.offerType === "item" && `& ${t("Items")}`}
                  </CardTitle>
                  {loading ? null : promotionDetails?.qualificationItems
                      .length === 0 ? (
                    <p className="text-center my-3">
                      {t("No categories found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Category")}</th>
                          {promotionDetails.offerType === "item" && (
                            <th>{t("Item")}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {promotionDetails?.qualificationItems?.map(
                          ({ categoryId, itemId }, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                {language === "sa"
                                  ? categoryId?.nameAr
                                  : categoryId?.name}
                              </td>
                              {promotionDetails.offerType === "item" && (
                                <td>
                                  {language === "sa"
                                    ? itemId?.nameAr
                                    : itemId?.name}
                                </td>
                              )}
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    </div>
  )
}

export default PromotionDetails
